import { useEffect, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useQrcInfo } from "../../../api/hooks/useQrcInfo"
import { QRC_TYPE } from "../../../api/interface"
import { setQrcInfo, startPaymentStatusPolling } from "../../../store/slices/c2bSlice"
import { ROUTES_PATHS } from "../../../utils/constants"
import StaticPayment from "./Static"
import DynamicPayment from "./Dynamic"
import C2BPushError from "../Error"
import LoadingOverlay from "../../../components/LoadingOverlay"
import { useCreateC2BPayment } from "../../../api/hooks/useCreateC2BPayment"

interface PaymentProps {
  onRetry: () => void,
  onClose: () => void
}

const C2BPushPayment = ({ onRetry, onClose }: PaymentProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { qrcId } = useParams()
  const { data: qrcInfo, isPending, isError, error } = useQrcInfo(qrcId)
  const { mutate: createPayment } = useCreateC2BPayment()
  const isQrcNotFound = error?.response?.status === 404

  useEffect(() => {
    if (!qrcInfo) return

    dispatch(setQrcInfo(qrcInfo))

    if (
      qrcInfo.qrcType !== QRC_TYPE.STATIC &&
      qrcInfo.qrcType !== QRC_TYPE.DYNAMIC
    ) {
      navigate(`/${ROUTES_PATHS.qr}/error`)
    }
  }, [qrcInfo, dispatch, navigate])

  const handlePay = useCallback((amount: number) => {
    if (qrcId && amount) {
      createPayment({ qrcId, amount }, {
        onSuccess: (result) => {
          dispatch(startPaymentStatusPolling(result.outcomePaymentId))
        },
      })
    }
  }, [createPayment, dispatch, qrcId])

  if (isPending) {
    return <LoadingOverlay disableOpacity />
  }

  if (qrcInfo?.qrcType === QRC_TYPE.STATIC) {
    return <StaticPayment onPay={handlePay} />
  }

  if (qrcInfo?.qrcType === QRC_TYPE.DYNAMIC) {
    return <DynamicPayment onPay={handlePay} />
  }

  if (isError) {
    return (
      <C2BPushError
        onRetry={onRetry}
        onClose={onClose}
        title={isQrcNotFound ? "QR-код недействителен." : "Операция не выполнена"}
        description={
          isQrcNotFound
            ? "Попробуйте отсканировать его ещё раз или запросите новый QR-код."
            : "Проверьте QR-код и попробуйте снова"
        }
      />
    )
  }

  return <LoadingOverlay disableOpacity />
}

export default C2BPushPayment
